import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Save, Eye, Mail, Copy, Pencil, Trash2, Star, StarOff } from "lucide-react";

const blankEditor = () => ({
    id: null,
    name: "",
    subject: "",
    html: "",
    is_default: false,
});

export default function EmailTemplates() {
    const [templates, setTemplates] = useState([]);
    const [variables, setVariables] = useState([]);
    const [fromEmail, setFromEmail] = useState("");
    const [builtIn, setBuiltIn] = useState({ subject: "", html: "" });
    const [loading, setLoading] = useState(true);

    const [editorOpen, setEditorOpen] = useState(false);
    const [editor, setEditor] = useState(blankEditor());
    const [saving, setSaving] = useState(false);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [preview, setPreview] = useState({ subject: "", html: "" });
    const [previewKey, setPreviewKey] = useState(0);

    const [testOpen, setTestOpen] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [testTplId, setTestTplId] = useState(null);
    const [sending, setSending] = useState(false);

    const fetchAll = async () => {
        const { data } = await api.get("/admin/email-templates");
        setTemplates(data.templates);
        setVariables(data.available_variables || []);
        setFromEmail(data.from_email || "");
        setBuiltIn(data.built_in_default);
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/email-templates");
                setTemplates(data.templates);
                setVariables(data.available_variables || []);
                setFromEmail(data.from_email || "");
                setBuiltIn(data.built_in_default);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const openCreate = () => {
        setEditor({
            id: null,
            name: "",
            subject: builtIn.subject,
            html: builtIn.html,
            is_default: templates.length === 0,
        });
        setEditorOpen(true);
    };

    const openEdit = (t) => {
        setEditor({
            id: t.id,
            name: t.name,
            subject: t.subject,
            html: t.html,
            is_default: !!t.is_default,
        });
        setEditorOpen(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                name: editor.name,
                subject: editor.subject,
                html: editor.html,
                is_default: editor.is_default,
            };
            if (editor.id) {
                await api.put(`/admin/email-templates/${editor.id}`, body);
                toast.success("Template updated");
            } else {
                await api.post("/admin/email-templates", body);
                toast.success("Template created");
            }
            setEditorOpen(false);
            await fetchAll();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const setDefault = async (t) => {
        if (t.is_default) return;
        try {
            await api.post(`/admin/email-templates/${t.id}/set-default`);
            toast.success(`"${t.name}" is now the default`);
            await fetchAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (t) => {
        if (!window.confirm(`Delete email template "${t.name}"?`)) return;
        try {
            await api.delete(`/admin/email-templates/${t.id}`);
            toast.success("Deleted");
            await fetchAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const showPreview = async () => {
        try {
            const { data } = await api.post("/admin/email-templates/preview", {
                name: editor.name || "preview",
                subject: editor.subject,
                html: editor.html,
            });
            setPreview(data);
            setPreviewOpen(true);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const sendTest = async () => {
        if (!testEmail) return;
        setSending(true);
        try {
            const { data } = await api.post("/admin/test-email", {
                to_email: testEmail,
                email_template_id: testTplId || null,
            });
            toast.success(`Sent! Resend id: ${data.resend_id?.slice(0, 12)}…`);
            setTestOpen(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    const insertVar = (key) => {
        const tag = `{{${key}}}`;
        navigator.clipboard.writeText(tag);
        toast.success(`Copied ${tag}`);
    };

    if (loading) return <div className="p-8 text-[#7A726D]">Loading…</div>;

    return (
        <div className="p-8 max-w-7xl mx-auto" data-testid="email-templates-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-semibold text-[#3E2723]">Email Templates</h1>
                    <p className="text-sm text-[#7A726D] mt-1">
                        Create multiple voucher email designs and assign them to specific bundles.
                        Bundles without a template assigned will use the default.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setTestOpen(true)} data-testid="open-test-email-btn">
                        <Mail className="w-4 h-4 mr-2" /> Send Test
                    </Button>
                    <Button onClick={openCreate} data-testid="new-template-btn"
                        className="bg-bc-orange hover:bg-[#D26A03] text-white">
                        <Plus className="w-4 h-4 mr-2" /> New Template
                    </Button>
                </div>
            </div>

            <Card className="mb-6 border-[#E5E0D8]">
                <CardContent className="p-4 text-sm">
                    <span className="text-[#7A726D] uppercase text-xs tracking-wider">From</span>{" "}
                    <code className="text-[#3E2723]">{fromEmail || "—"}</code>
                </CardContent>
            </Card>

            <Card className="border-[#E5E0D8]">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Subject</TableHead>
                            <TableHead>Last updated</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {templates.length === 0 && (
                            <TableRow><TableCell colSpan={4} className="text-center text-[#7A726D] py-8">
                                No templates yet.
                            </TableCell></TableRow>
                        )}
                        {templates.map(t => (
                            <TableRow key={t.id} data-testid={`tpl-row-${t.id}`}>
                                <TableCell>
                                    <div className="font-medium text-[#3E2723]">{t.name}</div>
                                    {t.is_default && (
                                        <Badge variant="secondary" className="mt-1 bg-bc-orange/10 text-bc-orange">
                                            <Star className="w-3 h-3 mr-1" /> Default
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell className="max-w-md truncate text-sm text-[#7A726D]">{t.subject}</TableCell>
                                <TableCell className="text-xs text-[#7A726D]">
                                    {t.updated_at?.slice(0, 19).replace("T", " ") || "—"}
                                </TableCell>
                                <TableCell className="text-right whitespace-nowrap">
                                    <Button size="sm" variant="ghost" onClick={() => setDefault(t)}
                                        disabled={t.is_default}
                                        data-testid={`set-default-${t.id}`}
                                        title={t.is_default ? "Already default" : "Make default"}>
                                        {t.is_default ? <Star className="w-4 h-4 text-bc-orange" /> : <StarOff className="w-4 h-4" />}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => openEdit(t)}
                                        data-testid={`edit-tpl-${t.id}`}>
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => remove(t)}
                                        disabled={t.is_default}
                                        data-testid={`delete-tpl-${t.id}`}
                                        title={t.is_default ? "Mark another default first" : "Delete"}>
                                        <Trash2 className={`w-4 h-4 ${t.is_default ? "" : "text-[#B24A45]"}`} />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* Editor dialog */}
            <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
                <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editor.id ? "Edit template" : "New template"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 lg:col-span-8 space-y-3">
                            <div>
                                <Label>Template name (internal)</Label>
                                <Input value={editor.name}
                                    onChange={e => setEditor({ ...editor, name: e.target.value })}
                                    placeholder="e.g. Premium Coffee Bundle Email"
                                    data-testid="editor-name" />
                            </div>
                            <div>
                                <Label>Subject line</Label>
                                <Input value={editor.subject}
                                    onChange={e => setEditor({ ...editor, subject: e.target.value })}
                                    data-testid="editor-subject" />
                            </div>
                            <div>
                                <Label>HTML body</Label>
                                <Textarea value={editor.html} rows={22}
                                    onChange={e => setEditor({ ...editor, html: e.target.value })}
                                    className="font-mono text-xs"
                                    data-testid="editor-html" />
                            </div>
                            <div className="flex items-center gap-2">
                                <input id="ed-default" type="checkbox" checked={editor.is_default}
                                    onChange={e => setEditor({ ...editor, is_default: e.target.checked })}
                                    data-testid="editor-is-default" />
                                <Label htmlFor="ed-default" className="text-sm">
                                    Make this the default template (used by bundles with no template assigned)
                                </Label>
                            </div>
                        </div>
                        <div className="col-span-12 lg:col-span-4">
                            <Card className="border-[#E5E0D8]">
                                <CardContent className="p-3">
                                    <h3 className="font-semibold text-[#3E2723] mb-2 text-sm">Available variables</h3>
                                    <ul className="space-y-1.5">
                                        {variables.map(v => (
                                            <li key={v.key}>
                                                <button type="button"
                                                    onClick={() => insertVar(v.key)}
                                                    data-testid={`var-${v.key}`}
                                                    className="w-full text-left p-2 border border-[#E5E0D8] rounded hover:bg-[#F7F3EE] transition-colors">
                                                    <div className="flex items-center justify-between">
                                                        <code className="text-bc-orange text-xs">{`{{${v.key}}}`}</code>
                                                        <Copy className="w-3 h-3 text-[#7A726D]" />
                                                    </div>
                                                    <div className="text-xs text-[#7A726D] mt-0.5">{v.description}</div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-3 border-t border-[#E5E0D8]">
                        <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
                        <Button variant="outline" onClick={showPreview} data-testid="editor-preview-btn">
                            <Eye className="w-4 h-4 mr-2" /> Preview
                        </Button>
                        <Button onClick={save} disabled={saving}
                            className="bg-bc-orange hover:bg-[#D26A03] text-white"
                            data-testid="editor-save-btn">
                            <Save className="w-4 h-4 mr-2" />
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Preview dialog */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Preview</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Subject (rendered)</Label>
                            <div className="p-3 border border-[#E5E0D8] rounded bg-[#F7F3EE] text-sm">
                                {preview.subject}
                            </div>
                        </div>
                        <div>
                            <Label>Body (rendered)</Label>
                            <div className="border border-[#E5E0D8] rounded overflow-hidden">
                                <iframe title="email-preview" srcDoc={preview.html}
                                    key={previewKey}
                                    className="w-full h-[60vh] bg-white" data-testid="preview-iframe" />
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Test email dialog */}
            <Dialog open={testOpen} onOpenChange={setTestOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Send test email</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Use template</Label>
                            <select
                                value={testTplId || ""}
                                onChange={e => setTestTplId(e.target.value || null)}
                                className="w-full p-2 border border-[#E5E0D8] rounded text-sm"
                                data-testid="test-tpl-select"
                            >
                                <option value="">— Default template —</option>
                                {templates.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}{t.is_default ? " (default)" : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label>To email</Label>
                            <Input type="email" value={testEmail}
                                onChange={e => setTestEmail(e.target.value)}
                                placeholder="you@example.com"
                                data-testid="test-email-to" />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
                            <Button onClick={sendTest} disabled={sending || !testEmail}
                                className="bg-bc-orange hover:bg-[#D26A03] text-white"
                                data-testid="send-test-email-btn">
                                {sending ? "Sending…" : "Send test"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
